import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import {
  TeamOutlined, ApartmentOutlined, BankOutlined,
  UserAddOutlined, BarChartOutlined,
} from '@ant-design/icons'
import StudentTab from './Tabs/StudentTab'
import CorporateTab from './Tabs/CorporateTab'
import InstituteTab from './Tabs/InstituteTab'
import AssessmentTab from './Tabs/AssessmentTab'
import FindCandidatesTab from './Tabs/FindCandidatesTab'

const TABS = [
  { key: 'students',       label: 'Candidate',         sup: '360°', icon: <TeamOutlined />,      accent: '#3249D7', bg: '#eef2ff' },
  { key: 'corporate',      label: 'Corporate',       sup: '360°', icon: <ApartmentOutlined />,  accent: '#7c3aed', bg: '#f5f3ff' },
  { key: 'institutes',     label: 'Institute',       sup: '360°', icon: <BankOutlined />,       accent: '#0891b2', bg: '#ecfeff' },
  { key: 'assessment',     label: 'Assessment',      sup: '360°', icon: <BarChartOutlined />,   accent: '#d97706', bg: '#fffbeb' },
  { key: 'findCandidates', label: 'Find Candidates', sup: '',     icon: <UserAddOutlined />,    accent: '#3249D7', bg: '#f0f3ff' },
]

const TAB_TITLES = {
  students:       { title: 'Candidate 360°',    sub: 'Complete analytics across students, corporates, and institutes' },
  corporate:      { title: 'Corporate 360°',  sub: 'Complete analytics across students, corporates, and institutes' },
  institutes:     { title: 'Institute 360°',  sub: 'Complete analytics across students, corporates, and institutes' },
  assessment:     { title: 'Assessment Analytics', sub: 'Utilisation, scores, rankings and institute performance' },
  findCandidates: { title: 'Find Candidates', sub: 'Match the right candidates to the right roles' },
}

// ── Shell Layout ─────────────────────────────────────────────────

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  background: #f5f6fb;
  height: 100%;
  min-height: 100vh;
`

const TopBar = styled.div`
  flex-shrink: 0;
  background: #ffffff;
  border-bottom: 1px solid #e8ecf4;
  padding: 18px 28px 0;
  z-index: 200;
`

const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 14px;
`

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 800;
  color: #1a1a2e;
  margin: 0 0 3px;
  letter-spacing: -0.3px;
`

const PageSubtitle = styled.p`
  font-size: 13px;
  color: #7b8499;
  margin: 0;
`

const DatePickerWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #7b8499;
  font-size: 13px;
  .ant-picker {
    border-radius: 10px;
    border-color: #e8ecf4;
    background: #f8f9fc;
    font-size: 13px;
  }
  .ant-picker:hover { border-color: #3249D7; }
`

const TabBar = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`

const TabBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 8px 8px 0 0;
  border: none;
  border-bottom: 3px solid ${p => p.$active ? p.$accent : 'transparent'};
  background: ${p => p.$bg};
  color: ${p => p.$active ? p.$accent : '#374151'};
  font-size: 13.5px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  .tab-icon { font-size: 14px; }
  &:hover {
    color: ${p => p.$accent};
    border-bottom-color: ${p => p.$accent}88;
  }
`

const TabBtnSup = styled.span`
  font-size: 0.65em;
  vertical-align: super;
  margin-left: 2px;
  font-weight: 800;
  color: ${p => p.$accent};
  letter-spacing: -0.3px;
`

const ScrollContent = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding: 24px 28px 32px;
`

// ── Component ────────────────────────────────────────────────────

const MetaDashboard = (props) => {
  const {
    fetchFilterOptions,
    fetchStudentFilterOptions,
    fetchFilterSkills,
    fetchFilterDepartments,
    fetchFilterDegrees,
    fetchFilterStates,
    fetchFilterColleges,
    fetchFilterCities,
    students, studentsLoading, studentSummary, studentProfile, studentProfileLoading,
    studentAssessments, studentAssessmentsLoading,
    fetchStudents, fetchStudentSummary, fetchStudentProfile, fetchStudentAssessments,
    corporateSummary, corporateGlobalSummary, corporateDepartments, corporateShortlist, corporateLoading, corporateCampusQuality,
    corporateDepartmentsTable, corporateDepartmentsTableLoading,
    corporateShortlistTable, corporateShortlistTableLoading,
    corporateCampusQualityTable, corporateCampusQualityTableLoading,
    corporatePlacementSummary, corporateCompanies, corporateCompaniesLoading,
    fetchCorporateData, fetchCorporateDepartmentsTable, fetchCorporateShortlistTable, fetchCorporateCampusQualityTable, fetchCorporatePlacementSummary, fetchCorporateCompanies,
    institutes, institutesLoading, instituteSummary, instituteSummaryLoading,
    institutePlacement, institutePlacementLoading,
    fetchInstitutes, fetchInstituteSummary, fetchInstitutePlacement,
    filterDepartments, filterStates, filterDegrees, filterColleges, filterSkills, filterCities,
  } = props

  const [activeTab, setActiveTab] = useState('students')

  useEffect(() => { fetchFilterOptions() }, [])

  const { title, sub } = TAB_TITLES[activeTab] || TAB_TITLES.students

  return (
    <Shell>
      <TopBar>
        <TopRow>
          <div>
            <PageTitle>{title}</PageTitle>
            <PageSubtitle>{sub}</PageSubtitle>
          </div>
        </TopRow>
        <TabBar>
          {TABS.map(t => (
            <TabBtn key={t.key} $active={activeTab === t.key} $accent={t.accent} $bg={t.bg} onClick={() => setActiveTab(t.key)}>
              <span className="tab-icon">{t.icon}</span>
              {t.label}
              {t.sup && <TabBtnSup $accent={t.accent}>{t.sup}</TabBtnSup>}
            </TabBtn>
          ))}
        </TabBar>
      </TopBar>

      <ScrollContent>
        {activeTab === 'students' && (
          <StudentTab
            students={students}
            studentsLoading={studentsLoading}
            studentSummary={studentSummary}
            studentProfile={studentProfile}
            studentProfileLoading={studentProfileLoading}
            studentAssessments={studentAssessments}
            studentAssessmentsLoading={studentAssessmentsLoading}
            filterDepartments={filterDepartments}
            filterStates={filterStates}
            filterDegrees={filterDegrees}
            filterColleges={filterColleges}
            filterSkills={filterSkills}
            filterCities={filterCities}
            fetchStudents={fetchStudents}
            fetchStudentSummary={fetchStudentSummary}
            fetchStudentProfile={fetchStudentProfile}
            fetchStudentAssessments={fetchStudentAssessments}
            fetchFilterOptions={fetchFilterOptions}
            fetchStudentFilterOptions={fetchStudentFilterOptions}
            fetchFilterSkills={fetchFilterSkills}
            fetchFilterDepartments={fetchFilterDepartments}
            fetchFilterDegrees={fetchFilterDegrees}
            fetchFilterStates={fetchFilterStates}
            fetchFilterColleges={fetchFilterColleges}
            fetchFilterCities={fetchFilterCities}
          />
        )}

        {activeTab === 'corporate' && (
          <CorporateTab
            corporateSummary={corporateSummary}
            corporateGlobalSummary={corporateGlobalSummary}
            corporateDepartments={corporateDepartments}
            corporateDepartmentsTable={corporateDepartmentsTable}
            corporateDepartmentsTableLoading={corporateDepartmentsTableLoading}
            corporateShortlist={corporateShortlist}
            corporateShortlistTable={corporateShortlistTable}
            corporateShortlistTableLoading={corporateShortlistTableLoading}
            corporateLoading={corporateLoading}
            corporateCampusQuality={corporateCampusQuality}
            corporateCampusQualityTable={corporateCampusQualityTable}
            corporateCampusQualityTableLoading={corporateCampusQualityTableLoading}
            corporatePlacementSummary={corporatePlacementSummary}
            corporateCompanies={corporateCompanies}
            corporateCompaniesLoading={corporateCompaniesLoading}
            filterDepartments={filterDepartments}
            filterStates={filterStates}
            filterDegrees={filterDegrees}
            fetchCorporateData={fetchCorporateData}
            fetchCorporateDepartmentsTable={fetchCorporateDepartmentsTable}
            fetchCorporateShortlistTable={fetchCorporateShortlistTable}
            fetchCorporateCampusQualityTable={fetchCorporateCampusQualityTable}
            fetchCorporatePlacementSummary={fetchCorporatePlacementSummary}
            fetchCorporateCompanies={fetchCorporateCompanies}
          />
        )}

        {activeTab === 'institutes' && (
          <InstituteTab
            institutes={institutes}
            institutesLoading={institutesLoading}
            instituteSummary={instituteSummary}
            instituteSummaryLoading={instituteSummaryLoading}
            institutePlacement={institutePlacement}
            institutePlacementLoading={institutePlacementLoading}
            filterStates={filterStates}
            filterDepartments={filterDepartments}
            filterDegrees={filterDegrees}
            fetchInstitutes={fetchInstitutes}
            fetchInstituteSummary={fetchInstituteSummary}
            fetchInstitutePlacement={fetchInstitutePlacement}
          />
        )}

        {activeTab === 'assessment' && <AssessmentTab />}

        {activeTab === 'findCandidates' && <FindCandidatesTab />}
      </ScrollContent>
    </Shell>
  )
}

export default MetaDashboard
